import {
  Component,
  EventEmitter,
  Input,
  NgModule,
  Output,
  ViewChild,
} from "@angular/core";
import {
  DevExtremeModule,
  DxCheckBoxModule,
  DxDataGridModule,
  DxFormComponent,
  DxFormModule,
  DxNumberBoxModule,
  DxSelectBoxModule,
  DxTagBoxModule,
  DxTextBoxModule,
} from "devextreme-angular";
import { formatMessage } from "devextreme/localization";
import { CommonModule } from "@angular/common";
import { OptionFormUiElementsData } from "../../../model/calculation/optionFormUiElementsData";

type OptionUIElementType = "full" | "cartons"

interface OptionUIElementSubcategory {
  key: string | null;
  subcategory: string;
  displayValue: string;
}

interface OptionUIElementField {
  key: string | null;
  displayValue: string;
  dependsOnField: string | null;
  dependsOnValue: string | null;
}

/**
 * Represents a list of subcategory objects, each defining a key-value pair
 * for a specific subcategory with an optional key and a display name.
 *
 * The key represents the value stored in the field items database.
 */
const SUB_CATEGORIES: OptionUIElementSubcategory[] = [
  { key: null, subcategory: "", displayValue: "" },
  { key: "BOOK_PAPER", subcategory: "BOOK_PAPER", displayValue: "Book Paper" },
  {
    key: "CASE_TYPE_ONE_PIECE",
    subcategory: "CASE_TYPE",
    displayValue: "Case Type 1-Piece",
  },
  {
    key: "CASE_TYPE_THREE_PIECE",
    subcategory: "CASE_TYPE",
    displayValue: "Case Type 3-Piece",
  },
  { key: "PREPRESS", subcategory: "PREPRESS", displayValue: "Prepress" },
  { key: "BINDERY", subcategory: "BINDERY", displayValue: "Bindery" },
];

/**
 * A mapping of subcategory keys to a list of ui input fields that belong to that subcategory.
 *
 * Example Keys:
 * - `BOOK_PAPER`: Contains fields related to book paper options. These fields are
 *   independent and do not depend on other values.
 * - `CASE_TYPE_ONE_PIECE`: Contains fields related to one-piece case types. Fields
 *   in this category depend on the `TYPE` field being set to `ONE_PIECE`.
 * - `CASE_TYPE_THREE_PIECE`: Contains fields related to three-piece case types. Fields
 *   in this category depend on the `TYPE` field being set to `THREE_PIECE`.
 *
 */
const INPUT_NAME_MAP: {
  [key: string]: OptionUIElementField[];
} = {
  BOOK_PAPER: [
    {
      key: null,
      displayValue: "",
      dependsOnField: null,
      dependsOnValue: null,
    },
    {
      key: "ENDSHEET",
      displayValue: "Endsheet - non printed",
      dependsOnField: null,
      dependsOnValue: null,
    },
  ],
  CASE_TYPE_ONE_PIECE: [
    {
      key: null,
      displayValue: "",
      dependsOnField: "TYPE",
      dependsOnValue: "ONE_PIECE",
    },
    {
      key: "BOARD_MATERIAL",
      displayValue: "Board Material",
      dependsOnField: "TYPE",
      dependsOnValue: "ONE_PIECE",
    },
    {
      key: "CASE_MATERIAL",
      displayValue: "Case Material",
      dependsOnField: "TYPE",
      dependsOnValue: "ONE_PIECE",
    },
    {
      key: "SPINE_STAMPING",
      displayValue: "Spine Stamping",
      dependsOnField: "TYPE",
      dependsOnValue: "ONE_PIECE",
    },
  ],
  CASE_TYPE_THREE_PIECE: [
    {
      key: null,
      displayValue: "",
      dependsOnField: "TYPE",
      dependsOnValue: "THREE_PIECE",
    },
    {
      key: "BOARD_MATERIAL",
      displayValue: "Board Material",
      dependsOnField: "TYPE",
      dependsOnValue: "THREE_PIECE",
    },
    {
      key: "CASE_MATERIAL",
      displayValue: "Case Material",
      dependsOnField: "TYPE",
      dependsOnValue: "THREE_PIECE",
    },
    {
      key: "SPINE_STAMPING",
      displayValue: "Spine Stamping",
      dependsOnField: "TYPE",
      dependsOnValue: "THREE_PIECE",
    },
  ],
  BINDERY: [
    {
      key: null,
      displayValue: "",
      dependsOnField: null,
      dependsOnValue: null,
    },
    {
      key: "PACK",
      displayValue: "Pack",
      dependsOnField: null,
      dependsOnValue: null,
    },
  ],
};

/**
 * OptionFormUiElementsEditorComponent is an Angular component for managing the
 * configuration and metadata of UI elements in an option form. It enables users to
 * configure properties such as subcategories, input field names, default values,
 * and sorting orders while adhering to dependencies between form fields.
 *
 * Properties:
 *
 * - value: Represents the current data structure for elements in the option form UI.
 *          This includes subcategory, input name, default value, sorting order, and
 *          dependency-related information.
 *
 * - requiredQuantityType: Specifies the type of required quantity, if applicable.
 *                         This input can be used to if the ui option needs a default value.
 *
 * - valueChange: Emits an event whenever the value of the UI elements data changes.
 *                Useful to propagate changes to parent components.
 *
 */
@Component({
  selector: "app-option-form-ui-elements-editor",
  templateUrl: "./option-form-ui-elements-editor.component.html",
  styleUrls: ["./option-form-ui-elements-editor.component.scss"],
})
export class OptionFormUiElementsEditorComponent {
  @ViewChild(DxFormComponent) form: DxFormComponent;

  formatMessage = formatMessage;

  /**
   * Represents the data structure for elements in the option form UI.
   *
   * This variable is used to store and manage the configuration
   * or metadata related to UI components in an option form.
   * It may include details such as element identifiers, labels,
   * types, and any other properties needed to render or process
   * the UI elements.
   *
   * @type {OptionFormUiElementsData}
   */
  private _value: OptionFormUiElementsData;
  @Input()
  get value(): OptionFormUiElementsData {
    return this._value;
  }
  set value(value: OptionFormUiElementsData) {
    if (this._value === value) {
      return;
    }
    this._value = value;
    this.populateInternalFieldsFromInput();
    this.inputNameChoosen = !!value?.inputName;
  }

  @Input() requiredQuantityType: string | undefined;

  _optionType: string | undefined;
  @Input()
  get optionType(): string | undefined {
    return this._optionType;
  }
  set optionType(value: string | undefined) {
    this._optionType = value;
    switch (value) {
      case "CARTONS":
        this.uiInputType = "cartons";
        break;
      default:
        this.uiInputType = "full";
        break;
    }
  }

  @Output()
  valueChange = new EventEmitter<OptionFormUiElementsData>(); // The output event

  private _selectedSubcategory: OptionUIElementSubcategory | undefined;
  get selectedSubcategory(): OptionUIElementSubcategory | undefined {
    return this._selectedSubcategory;
  }
  set selectedSubcategory(value: OptionUIElementSubcategory | undefined) {
    const changed = this._selectedSubcategory !== value;
    if (changed) {
      this._selectedSubcategory = value;
      this.subcategoryChanged();
      this.emitValueChange();
    }
  }
  private _selectedInputName: OptionUIElementField | undefined;
  get selectedInputName(): OptionUIElementField | undefined {
    return this._selectedInputName;
  }
  set selectedInputName(value: OptionUIElementField | undefined) {
    this._selectedInputName = value;
    this.emitValueChange();
  }
  // default option type fields
  private _selectedIsDefault: boolean | null | undefined;
  get selectedIsDefault(): boolean | null | undefined {
    return this._selectedIsDefault;
  }
  set selectedIsDefault(value: boolean | null | undefined) {
    this._selectedIsDefault = value;
    this.emitValueChange();
  }
  private _selectedDefaultValue: string;
  get selectedDefaultValue(): string {
    return this._selectedDefaultValue;
  }
  set selectedDefaultValue(value: string) {
    this._selectedDefaultValue = value;
    this.emitValueChange();
  }
  private _selectedSortOrder: number;
  get selectedSortOrder(): number {
    return this._selectedSortOrder;
  }
  set selectedSortOrder(value: number) {
    this._selectedSortOrder = value;
    this.emitValueChange();
  }
  // cartons option type fields
  private _selectedDefaultCartonValue: string;
  @Input()
  get selectedDefaultCartonValue() {
    return this._selectedDefaultCartonValue;
  }
  set selectedDefaultCartonValue(value: string) {
    this._selectedDefaultCartonValue = value;
    this.emitValueChange();
  }

  emitValueChange() {
    const valueChangeEvent = <OptionFormUiElementsData>{
      subcategory: this.selectedSubcategory?.subcategory,
      inputName: this.selectedInputName?.key,
      isDefault: this.selectedIsDefault,
      defaultValue: this.selectedDefaultValue,
      sortOrder: this.selectedSortOrder,
      dependsOnField: this.selectedInputName?.dependsOnField,
      dependsOnValue: this.selectedInputName?.dependsOnValue,
      defaultCartonValue: this.selectedDefaultCartonValue,
    };

    this.valueChange.emit(valueChangeEvent);
  }

  uiInputType: OptionUIElementType = "full";

  subcategoryDataSource = SUB_CATEGORIES;
  inputNameDataSource: OptionUIElementField[] = [];

  inputNameItemDisabled: boolean = true;
  inputNameChoosen = false;

  constructor() {
    this.translateLocaleValue = this.translateLocaleValue.bind(this);
    this.subcategoryChanged = this.subcategoryChanged.bind(this);
  }

  /**
   * Populates internal fields based on the provided input data (_value). If the input data includes a subcategory
   * and matches predefined conditions, it updates the related fields including selected subcategory, input name,
   * default values, sort order, and data source. If no matching subcategory is found or input data is missing,
   * it clears the internal fields.
   *
   * @return {void} This method does not return any value.
   */
  private populateInternalFieldsFromInput() {
    if (this._value && this._value.subcategory) {
      // ui element assigned to option, populate internal fields
      const possibleSubcategories = SUB_CATEGORIES.filter(
        (x) => x.subcategory === this._value.subcategory
      );
      for (const subcategory of possibleSubcategories) {
        const inputNamesForSubcategory = INPUT_NAME_MAP[subcategory.key || ""];
        const possibleInputNames = inputNamesForSubcategory?.find(
          (elementField) =>
            elementField.key === this._value.inputName &&
            elementField.dependsOnField === this._value.dependsOnField &&
            elementField.dependsOnValue === this._value.dependsOnValue
        );
        if (possibleInputNames) {
          this._selectedSubcategory = subcategory;
          this._selectedInputName = possibleInputNames;
          this._selectedIsDefault = this._value.isDefault;
          this._selectedDefaultValue = this._value.defaultValue || "";
          this._selectedSortOrder = this._value.sortOrder;
          this.inputNameDataSource = [...inputNamesForSubcategory];
          this.inputNameItemDisabled = !subcategory.key;
          this.selectedDefaultCartonValue = this._value.defaultCartonValue || "";
          break;
        }
      }
    } else {
      // no ui element assigned to option, clear all internal fields
      this.clearInternalFieldsWithoutUpdateEvent();
      this._selectedSubcategory = undefined;
    }
  }

  /**
   * Handles changes to the selected subcategory and updates related data sources and states.
   *
   * This method clears internal fields, updates the input name data source based on the
   * selected subcategory, and disables or enables input name items accordingly.
   *
   * @return {void} This method does not return a value.
   */
  private subcategoryChanged() {
    this.clearInternalFieldsWithoutUpdateEvent();

    if (
      this._selectedSubcategory?.key &&
      INPUT_NAME_MAP[this._selectedSubcategory.key] !== undefined
    ) {
      this.inputNameDataSource = [
        ...INPUT_NAME_MAP[this._selectedSubcategory.key],
      ];
    } else {
      this.inputNameDataSource = [];
    }
    this.inputNameItemDisabled = !this._selectedSubcategory?.key;
  }

  private clearInternalFieldsWithoutUpdateEvent() {
    this._selectedInputName = undefined;
    this._selectedIsDefault = false;
    this._selectedDefaultValue = "";
    this._selectedSortOrder = 0;
    this._selectedDefaultCartonValue = "";
  }

  translateLocaleValue(data): string {
    return formatMessage(`locale.${data}`);
  }
}

@NgModule({
  imports: [
    DxFormModule,
    CommonModule,
    DxCheckBoxModule,
    DxTagBoxModule,
    DxSelectBoxModule,
    DxDataGridModule,
    DevExtremeModule,
    DxNumberBoxModule,
    DxTextBoxModule,
  ],
  declarations: [OptionFormUiElementsEditorComponent],
  exports: [OptionFormUiElementsEditorComponent],
})
export class OptionFormUiElementsEditorModule {}
